import * as React from 'react';
import { cn } from '@/lib/utils';
import { Card, CardBody, CardContent } from '@/components/ui/card';

const CourseAbout = React.forwardRef(({ className, backgroundUrl, children, cardBodyClassName, ...props }, ref) => {
  return (
    <Card
      ref={ref}
      className={cn('c-course-about', className)}
    >
      <CardContent>
        <CardBody
          {...props}
          className={cn(cardBodyClassName)}
        >
          {children}
        </CardBody>
      </CardContent>
    </Card>
  );
});
CourseAbout.displayName = 'CourseAbout';

const CourseAboutHeader = React.forwardRef(({ className, noSeparator = false, ...props }, ref) => (
  <header
    ref={ref}
    className={cn(
      'c-course-about-header',
      // Estilo base
      'border-b border-solid border-neutral-lightest',
      'pb-5',
      // Variantes
      noSeparator && 'border-none pb-0',
      className
    )}
    {...props}
  />
));
CourseAboutHeader.displayName = 'CourseAboutHeader';

const CourseAboutHeaderContainer = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-about-header-container',
      // Estilo base
      'flex',
      'items-start',
      'flex-wrap',
      'gap-[20%]',
      'mb-5',
      className
    )}
    {...props}
  />
));
CourseAboutHeaderContainer.displayName = 'CourseAboutHeaderContainer';

const CourseAboutHeaderContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-about-header-content',
      // Estilo base
      'w-28',
      'flex-grow',
      'flex',
      'flex-col',
      'gap-5',
      className
    )}
    {...props}
  />
));
CourseAboutHeaderContent.displayName = 'CourseAboutHeaderContent';

const CourseAboutInfos = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-about-infos',
      // Estilo base
      'flex flex-wrap items-center justify-between gap-10',
      className
    )}
    {...props}
  />
));
CourseAboutInfos.displayName = 'CourseAboutInfos';

const CourseAboutRating = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-about-rating',
      // Estilo base
      'mb-0',
      'font-light',
      'text-sm',
      'text-neutral',
      className
    )}
    {...props}
  />
));
CourseAboutRating.displayName = 'CourseAboutRating';

const CourseAboutRatingText = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'c-course-about-rating-text',
      // Estilo base
      'mb-0',
      'font-light',
      'text-sm',
      'text-neutral',
      className
    )}
    {...props}
  />
));
CourseAboutRatingText.displayName = 'CourseAboutRatingText';

const CourseAboutDeadline = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-about-deadline',
      // Estilo base
      'bg-[#F1F5FA]',
      'p-2',
      'text-primary-dark',
      'flex',
      'items-center',
      'justify-center',
      'gap-1',
      'rounded',
      'leading-tight',
      '[&_svg]:shrink-0',
      className
    )}
    {...props}
  />
));
CourseAboutDeadline.displayName = 'CourseAboutDeadline';

const CourseAboutDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'c-course-about-description',
      // Estilo base
      'mb-4 break-words text-sm font-light text-[#5D6A86]',
      className
    )}
    {...props}
  />
));
CourseAboutDescription.displayName = 'CourseAboutDescription';

const CourseAboutBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-course-about-body', 'flex flex-col gap-8', className)}
    {...props}
  />
));
CourseAboutBody.displayName = 'CourseAboutBody';

// Título de seção do bloco "sobre". h2 por hierarquia (o h5 antigo pulava três
// níveis a partir do h2 do banner), mas o tamanho vem de text-base explícito:
// sem ele o h2 do App.css aplicaria text-2xl e a seção mudaria de peso na tela.
const CourseAboutTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn('c-course-about-title', 'text-base', 'font-extrabold', 'text-primary-dark', 'mb-2', className)}
    {...props}
  />
));
CourseAboutTitle.displayName = 'CourseAboutTitle';

// Filho do CourseAboutTitle (ex.: "Texto 1" dentro de "Textos motivadores").
// text-base/font-bold já estavam explícitos e continuam vencendo o h3 do
// App.css, então a troca de tag não altera o tamanho renderizado.
const CourseAboutSubtitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('c-course-about-subtitle', 'mb-0 text-base font-bold text-neutral-dark', className)}
    {...props}
  />
));
CourseAboutSubtitle.displayName = 'CourseAboutSubtitle';

export {
  CourseAbout,
  CourseAboutHeader,
  CourseAboutHeaderContainer,
  CourseAboutHeaderContent,
  CourseAboutInfos,
  CourseAboutRating,
  CourseAboutRatingText,
  CourseAboutDeadline,
  CourseAboutDescription,
  CourseAboutBody,
  CourseAboutTitle,
  CourseAboutSubtitle
};
